import { Col, Flex, Form, Typography } from "antd";
import { CreateBidForm } from "components";
import { CHANGE_WALLET_ADDRESS_MESSAGE } from "constants/network";
import {
  AgentProposalRequest,
  NETWORK_TEXT,
  NOTIFICATION_TYPE_ENUM,
  REQUEST_CHECK_STATUS_ENUM,
  RISK_LEVEL_ENUM,
  WalletRiskLevel,
  CONTRACT_CURRENCY_ENUM,
} from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";
import { agentProposal } from "services";
import { useStores } from "stores";
import { CustomButton } from "ui";
import { getFirstOption, normalizeDecimalSeparator, parseErrorMessage, useWalletRisk } from "utils";

enum SEND_STEP {
  SEND = "SEND",
  CHECKING_WALLET = "CHECKING_WALLET",
}

export const ApproveBidModal = observer(() => {
  const { actionModals, bidCard, user } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [sendStep, setSendStep] = useState(SEND_STEP.SEND);

  const [form] = Form.useForm();

  const [isWalletChecked, setIsWalletChecked] = useState(false);
  const [canCheckWallet, setCanCheckWallet] = useState(false);
  const [checkedWallet, setCheckedWallet] = useState<string | null>(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [savedRiskLevel, setSavedRiskLevel] = useState<WalletRiskLevel>();

  const [storedFormValues, setStoredFormValues] = useState<any>({});

  const { riskLevel, riskStatus } = useWalletRisk(
    {
      walletAddress: form.getFieldsValue().destinationWallet || "",
      requestId: bidCard.bid.id,
      userId: user.userInfo?.id,
    },
    canCheckWallet
  );
  const isImpBid = bidCard.bid.contract.direction === "IMP";

  useEffect(() => {
    if (riskLevel && riskStatus) {
      setSavedRiskLevel(riskLevel);
      setIsWalletChecked(true);
      setCheckedWallet(form.getFieldsValue().destinationWallet || "");
      setCanCheckWallet(false);
      setLoading(false);
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.SUCCESS) {
        const currentFormValues = form.getFieldsValue();
        setStoredFormValues(currentFormValues);
        setSendStep(SEND_STEP.CHECKING_WALLET);
      }
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.APPROVE_NEEDED) {
        setLoading(false);
        if (![RISK_LEVEL_ENUM.MEDIUM, RISK_LEVEL_ENUM.TIMEOUT].includes(riskLevel)) {
          setButtonDisabled(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskLevel]);

  const handleCloseModal = () => {
    if (sendStep === SEND_STEP.CHECKING_WALLET) {
      setSendStep(SEND_STEP.SEND);
    } else {
      actionModals.closeAll();
    }
  };

  const getSubmitButtonText = (): string => {
    if (bidCard.bid.contract.direction === "IMP" && sendStep === SEND_STEP.SEND) {
      return "Далее";
    }
    return "Подтвердить";
  };

  const getCancelButtonText = (): string => {
    switch (sendStep) {
      case SEND_STEP.CHECKING_WALLET:
        return "Вернуться к форме";
      default:
        return "Отмена";
    }
  };

  const sendAgentProposal = async () => {
    setLoading(true);

    const currentFormValues = form.getFieldsValue();
    const values = { ...storedFormValues, ...currentFormValues };
    const dataToSend: AgentProposalRequest = {
      ...values,
      amount: parseFloat(normalizeDecimalSeparator(values.amount)),
    };

    await agentProposal({ id: bidCard.bid.id }, dataToSend)
      .then(() => {
        actionModals.closeAll();
        openNotification({
          message: "Условия успешно предложены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      })
      .finally(() => setLoading(false));
  };

  const getSubmitButtonTooltip = () => {
    if (buttonDisabled) {
      return CHANGE_WALLET_ADDRESS_MESSAGE;
    }
    return undefined;
  };

  const onFinish = () => {
    if (sendStep === SEND_STEP.SEND) {
      const currentFormValues = form.getFieldsValue();
      setStoredFormValues(currentFormValues);

      if (bidCard.bid.contract.direction === "IMP") {
        const walletChecked = isWalletChecked && checkedWallet === form.getFieldsValue().destinationWallet;
        if (walletChecked) {
          setSendStep(SEND_STEP.CHECKING_WALLET);
        } else {
          setLoading(true);

          setIsWalletChecked(false);
          setCanCheckWallet(true);
        }
      } else {
        sendAgentProposal();
      }
    }
    if (sendStep === SEND_STEP.CHECKING_WALLET) {
      sendAgentProposal();
    }
  };

  return (
    <>
      <Flex vertical>
        <Form
          form={form}
          name="agentProposal"
          initialValues={{
            currency: getFirstOption(CONTRACT_CURRENCY_ENUM),
            network: getFirstOption(NETWORK_TEXT),
          }}
          onFinish={onFinish}
        >
          <Flex vertical hidden={sendStep === SEND_STEP.CHECKING_WALLET}>
            <CreateBidForm
              title="Введите параметры агентского договора"
              disabled={loading}
              addressInputVisible={bidCard.bid.contract.direction === "IMP"}
              hidden={sendStep === SEND_STEP.CHECKING_WALLET}
              onWalletChange={() => {
                if (buttonDisabled) {
                  setButtonDisabled(false);
                  setIsWalletChecked(false);
                }
              }}
              showRiskAlert={isWalletChecked && !loading}
              riskLevel={savedRiskLevel}
            />
          </Flex>

          {sendStep === SEND_STEP.CHECKING_WALLET && (
            <Flex vertical gap="middle">
              <Typography.Title level={1} className="margin-none">
                Внимательно проверьте введенный адрес криптокошелька
              </Typography.Title>
              <Typography.Text className="text-middle">{form.getFieldValue("destinationWallet")}</Typography.Text>
            </Flex>
          )}

          <Form.Item className="margin-top-md">
            <Flex gap="large">
              <CustomButton
                htmlType="submit"
                disabled={loading || buttonDisabled}
                loading={loading}
                tooltip={getSubmitButtonTooltip()}
              >
                {getSubmitButtonText()}
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCloseModal}
              >
                {getCancelButtonText()}
              </CustomButton>
            </Flex>
            {!isWalletChecked && isImpBid && loading && sendStep === SEND_STEP.SEND && (
              <Col className="margin-top-xs">
                <Typography.Text className="text-description">Идет проверка кошелька</Typography.Text>
              </Col>
            )}
          </Form.Item>
        </Form>
      </Flex>
    </>
  );
});
